import { useEffect, useState } from "react";
import { Badge } from "@zweb/ui";
import { apiFetch } from "../lib/api";

interface Ticket {
  id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

const statusLabels: Record<string, string> = {
  open: "Aberto",
  in_progress: "Em Andamento",
  waiting_client: "Aguardando Cliente",
  waiting_support: "Aguardando Suporte",
  resolved: "Resolvido",
  closed: "Fechado",
};

const priorityLabels: Record<string, string> = {
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  urgent: "Urgente",
};

const statusColors: Record<string, string> = {
  open: "bg-accent-100 text-accent-800",
  in_progress: "bg-yellow-100 text-yellow-800",
  waiting_client: "bg-orange-100 text-orange-800",
  waiting_support: "bg-accent-100 text-accent-800",
  resolved: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-800",
};

const priorityColors: Record<string, string> = {
  low: "bg-gray-100 text-gray-800",
  medium: "bg-accent-100 text-accent-800",
  high: "bg-orange-100 text-orange-800",
  urgent: "bg-red-100 text-red-800",
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      window.location.href = "/login";
      return;
    }

    apiFetch("/api/tickets")
      .then((json) => {
        if (json.success) {
          setTickets(json.data);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const json = await apiFetch("/api/tickets", {
        method: "POST",
        body: JSON.stringify({ title, description, priority, category }),
      });
      if (json.success) {
        setShowNewTicket(false);
        setTitle("");
        setDescription("");
        setPriority("medium");
        setCategory("");
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao criar chamado:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-accent-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
        <div className="container flex h-16 items-center justify-between">
          <a href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent-600 text-white font-bold">
              Z
            </div>
            <span className="text-xl font-bold">ZWeb</span>
          </a>
          <nav className="flex items-center gap-6">
            <a href="/dashboard" className="text-sm font-medium hover:text-accent-600">Dashboard</a>
            <a href="/chamados" className="text-sm font-medium text-accent-600">Chamados</a>
            <a href="/downloads" className="text-sm font-medium hover:text-accent-600">Downloads</a>
            <a href="/licencas" className="text-sm font-medium hover:text-accent-600">Licenças</a>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                window.location.href = "/";
              }}
              className="text-sm text-red-600 hover:underline"
            >
              Sair
            </button>
          </nav>
        </div>
      </header>

      <main className="container py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Chamados</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Gerencie seus chamados de suporte
            </p>
          </div>
          <button
            onClick={() => setShowNewTicket(true)}
            className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
          >
            Novo Chamado
          </button>
        </div>

        {showNewTicket && (
          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-gray-950">
            <h2 className="text-xl font-semibold">Novo Chamado</h2>
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium">Título</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium">Descrição</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  required
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium">Prioridade</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                  >
                    <option value="low">Baixa</option>
                    <option value="medium">Média</option>
                    <option value="high">Alta</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium">Categoria</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20 dark:border-gray-700 dark:bg-gray-900"
                    placeholder="Ex: Dúvida, Bug, Sugestão"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  type="submit"
                  className="rounded-lg bg-accent-600 px-4 py-2 font-medium text-white hover:bg-accent-700"
                >
                  Criar Chamado
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewTicket(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 font-medium hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-900"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {tickets.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-950">
              <p className="text-gray-600 dark:text-gray-400">
                Nenhum chamado encontrado. Clique em "Novo Chamado" para criar um.
              </p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <a
                key={ticket.id}
                href={`/chamados/${ticket.id}`}
                className="block rounded-2xl border border-gray-200 bg-white p-6 transition-shadow hover:shadow-md dark:border-gray-800 dark:bg-gray-950"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{ticket.title}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {ticket.description.substring(0, 150)}
                      {ticket.description.length > 150 && "..."}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Badge className={statusColors[ticket.status]}>
                        {statusLabels[ticket.status]}
                      </Badge>
                      <Badge className={priorityColors[ticket.priority]}>
                        {priorityLabels[ticket.priority]}
                      </Badge>
                      {ticket.category && (
                        <Badge variant="secondary">{ticket.category}</Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(ticket.createdAt).toLocaleDateString("pt-BR")}
                  </div>
                </div>
              </a>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
